import { FC } from 'react';
import {
  ListTotal,
  Table,
  TableToolbar,
  TableWrapper,
  Tile,
} from 'melp-design/components';
import { Divider, Skeleton, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  AchievementsFilter,
  DateRangeFilter,
  EmployeesFilter,
  FilterButton,
  Filters,
  OpenSearchFilter,
  OrgStructureFilter,
  SelectFilter,
} from 'components/filters';
import { DownloadXlsAction } from 'components/actions';
import { useSearchParams } from 'utils/navigation';
import {
  recognitionLeaderboardFiltersSchema,
  useExportToXls,
  useRecognitionLeaderboard,
} from 'store/recognition-leaderboard';
import { recognitionTypes, usePointsLabel } from 'store/recognitions';
import { APP } from 'config';
import { RecipientsTabsSwitcher } from 'containers/recognitions';
import { useMe } from 'state/Administrators';
import { EmployeeCell } from 'components/table';
import { CompanyScopeInfoMessage } from 'containers/employees';

export const RecognitionsLeaderboardList: FC = () => {
  const { t } = useTranslation();
  const pointsLabel = usePointsLabel();
  const { hasViewPermission } = useMe();
  const { searchParams: filters, navigate } = useSearchParams(
    recognitionLeaderboardFiltersSchema,
  );

  const { data: leaderboardEmployees, isLoading } =
    useRecognitionLeaderboard(filters);
  const { mutate: exportToXls, isLoading: isExportingToXls } = useExportToXls();

  return (
    <TableWrapper>
      <TableToolbar>
        <Stack gap={1}>
          <RecipientsTabsSwitcher />

          <CompanyScopeInfoMessage />

          <Divider />

          <Stack direction="row" alignItems="center" gap="15px">
            {isLoading ? (
              <>
                <Skeleton variant="rounded" width={200} height={81} />
                <Skeleton variant="rounded" width={200} height={81} />
                <Skeleton variant="rounded" width={200} height={81} />
              </>
            ) : leaderboardEmployees ? (
              <>
                <Tile
                  title={t('recognitions.received.label', {
                    currency: pointsLabel,
                  })}
                  items={[leaderboardEmployees.meta.receivedPoints]}
                />
                <Tile
                  title={t('recognitions.reward_points.label', {
                    recognitionCurrency: pointsLabel,
                  })}
                  items={[leaderboardEmployees.meta.rewardedPoints]}
                />
                <Tile
                  title={t('recognitions.total.label')}
                  items={[leaderboardEmployees.meta.totalPoints]}
                />
              </>
            ) : null}
          </Stack>

          <Divider />

          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            gap={1}
          >
            <ListTotal total={leaderboardEmployees?.total} />

            <Stack direction="row" alignItems="center" gap={1}>
              <Filters
                value={filters}
                initialValues={recognitionLeaderboardFiltersSchema.parse({})}
                onChange={(v) => navigate({ params: { ...v, page: 1 } })}
              >
                {({ value, initialValues, setFilter }) => (
                  <>
                    {hasViewPermission(['employees']) ? (
                      <FilterButton
                        label={t('entity_type.employee')}
                        value={value.employees}
                        initialValue={initialValues.employees}
                        onChange={(v) => setFilter('employees', v)}
                      >
                        {({ value, applyFilter, clearFilter, close }) => (
                          <EmployeesFilter
                            value={{ value }}
                            onApplyFilter={({ value }) => applyFilter(value)}
                            onClearFilter={clearFilter}
                            onCancel={close}
                          />
                        )}
                      </FilterButton>
                    ) : null}

                    <FilterButton
                      label={t('home.organisationStructure_title')}
                      value={value.orgStructure}
                      initialValue={initialValues.orgStructure}
                      onChange={(v) => setFilter('orgStructure', v)}
                      contentMaxWidth={420}
                    >
                      {({ value, applyFilter, clearFilter, close }) => (
                        <OrgStructureFilter
                          value={value}
                          onApplyFilter={applyFilter}
                          onClearFilter={clearFilter}
                          onCancel={close}
                        />
                      )}
                    </FilterButton>

                    <FilterButton
                      label={t('common.date')}
                      value={value.date}
                      initialValue={initialValues.date}
                      onChange={(v) => setFilter('date', v)}
                    >
                      {({ value, applyFilter, clearFilter, close }) => (
                        <DateRangeFilter
                          value={value}
                          onApply={applyFilter}
                          onClear={clearFilter}
                          onCancel={close}
                        />
                      )}
                    </FilterButton>

                    <FilterButton
                      label={t('integrations.type')}
                      value={value.type}
                      initialValue={initialValues.type}
                      onChange={(v) => setFilter('type', v)}
                    >
                      {({ value, applyFilter, clearFilter, close }) => (
                        <SelectFilter
                          value={{ value }}
                          onApplyFilter={({ value }) => applyFilter(value)}
                          onClearFilter={clearFilter}
                          onCancel={close}
                          options={recognitionTypes.map((type) => ({
                            key: type,
                            label: t(`recognitions.type.${type}`),
                          }))}
                          searchable={false}
                          multiple={false}
                        />
                      )}
                    </FilterButton>

                    <FilterButton
                      label={t('recognition.feedFiltersType')}
                      value={value.achievements}
                      initialValue={initialValues.achievements}
                      onChange={(v) => setFilter('achievements', v)}
                    >
                      {({ value, applyFilter, clearFilter, close }) => (
                        <AchievementsFilter
                          value={{ value }}
                          onApplyFilter={({ value }) => applyFilter(value)}
                          onClearFilter={clearFilter}
                          onCancel={close}
                        />
                      )}
                    </FilterButton>

                    <OpenSearchFilter
                      value={filters.search}
                      onApplyFilter={(v) => setFilter('search', v)}
                      placeholder={t('common.search')}
                    />
                  </>
                )}
              </Filters>

              <Divider orientation="vertical" variant="middle" flexItem />

              <DownloadXlsAction
                listLength={leaderboardEmployees?.total ?? 0}
                exportToExcel={() => exportToXls({ filters })}
                loading={isExportingToXls}
              />
            </Stack>
          </Stack>
        </Stack>
      </TableToolbar>

      <Table
        data={leaderboardEmployees?.items ?? []}
        isLoading={isLoading}
        columns={[
          {
            id: 'name',
            header: t('entity_type.employee'),
            cell: ({ row: { original: employee } }) => (
              <EmployeeCell employee={employee} />
            ),
          },
          {
            id: 'receivedPoints',
            header: t('recognitions.received.label', {
              currency: pointsLabel,
            }),
            cell: ({ row: { original: employee } }) => employee.receivedPoints,
            meta: { sort: true, align: 'right' },
          },
          {
            id: 'rewardedPoints',
            header: t('recognitions.rewarded.label', {
              currency: pointsLabel,
            }),
            cell: ({ row: { original: employee } }) => employee.rewardedPoints,
            meta: { sort: true, align: 'right' },
          },
          {
            id: 'totalPoints',
            header: t('recognitions.total.label'),
            cell: ({ row: { original: employee } }) => employee.totalPoints,
            meta: { sort: true, align: 'right' },
          },
        ]}
        sort={{
          value: filters.sort,
          onChange: (v) => navigate({ params: { sort: v } }),
        }}
        pagination={
          leaderboardEmployees && {
            count: leaderboardEmployees.total,
            page: leaderboardEmployees.page,
            pageSize: leaderboardEmployees.pageSize,
            pageSizeOptions: APP.pagination.defaults.pageSize,
            onChange: (page, pageSize) => {
              navigate({ params: { page, pageSize } });
            },
          }
        }
      />
    </TableWrapper>
  );
};
